import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  AddComment as AddCommentIcon,
  ChecklistOutlined as ChecklistIcon,
  Close as CloseIcon,
  Forum as ForumIcon,
} from "@mui/icons-material";
import { send_request } from "../../utils/Request";
import { pageHeaderSx } from "../../constants/pageLayout";
import AgentSessionsPanel from "./AgentSessionsPanel";
import SessionRow from "./SessionRow";
import useAgentStore from "./useAgentStore";
import {
  extractRequestErrorMessage,
  isRequestError,
} from "./requestErrorUtils";

export const EMBEDDED_AGENT_PANEL_WIDTH = 420;

/**
 * When the task-queue planner panel opens we widen the embedded panel past the
 * side-by-side breakpoint (FORM_PANEL_STACK_BREAKPOINT = 900) so the queue
 * renders as a full-height column to the side of the chat instead of stacking
 * below it and getting crammed out of view as the conversation grows. The chat
 * column stays pinned at EMBEDDED_AGENT_PANEL_WIDTH (see splitChatFixedWidth),
 * so all of the extra width here flows to the planner panel.
 */
export const EMBEDDED_AGENT_PANEL_QUEUE_OPEN_WIDTH = 940;

export const embeddedAgentPanelBaseSx = {
  width: EMBEDDED_AGENT_PANEL_WIDTH,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  position: "relative",
  flexShrink: 0,
  transition: "width 0.18s ease",
};

const panelDividerSx = {
  right: {
    borderRight: (theme) => `1px solid ${theme.palette.divider}`,
    zIndex: 5,
  },
  left: {
    borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
    zIndex: 5,
  },
  none: {
    zIndex: "auto",
  },
};

/** Workflow/table embed: panel on the left, divider on the right edge */
export const embeddedAgentPanelSx = {
  ...embeddedAgentPanelBaseSx,
  borderRight: (theme) => `1px solid ${theme.palette.divider}`,
  zIndex: 5,
};

const DEFAULT_CHAT_UI_POLICY = {
  showSystemPromptInChat: false,
  showToolCallsAndOutputsInChat: true,
  showInterAgentMessagesInChat: false,
};

/**
 * Named presentation presets for embedding the panel. Callers pick a single `variant` instead of
 * wiring the individual layout props (divider edge, header visibility, fixed width, style hooks) by
 * hand.
 *
 * - `panel` (default): standalone side panel with its own header, session switcher, and edge divider
 *   — used by the artifact agent chat and other slide-out embeds.
 * - `transcript`: bare, full-width read-through embed for the ticket stage-history timeline — no
 *   panel/session headers, no divider, and the stage-history transcript style hooks.
 */
const PANEL_VARIANTS = {
  panel: {
    panelDividerEdge: "right",
    hidePanelHeader: false,
    hideSessionHeader: false,
    splitChatFixedWidth: EMBEDDED_AGENT_PANEL_WIDTH,
    panelClassName: null,
    conversationClassName: null,
    messagesClassName: null,
    panelSx: {},
  },
  transcript: {
    panelDividerEdge: "none",
    hidePanelHeader: true,
    hideSessionHeader: true,
    splitChatFixedWidth: null,
    panelClassName: "stage-history-transcript__panel",
    conversationClassName: "stage-history-transcript__conversation",
    messagesClassName: "stage-history-transcript__messages",
    panelSx: { width: "100%", borderRight: "none" },
  },
};

export default function ScopedAgentChatPanel({
  agentId: providedAgentId = null,
  panelTitle,
  agentName,
  agentDescription,
  agentModel,
  provisionUrl,
  provisionAgent,
  provisionErrorMessage = "Failed to provision agent",
  sessionFilter,
  emptySessionsMessage = "No sessions yet.",
  onClose,
  chatContext,
  chatUiPolicy = DEFAULT_CHAT_UI_POLICY,
  composerDisabled = false,
  welcomePanelContent = null,
  examplePrompts = [],
  initialComposerDraft = null,
  autoStartInitialPrompt = false,
  onTemplateLaunchConsumed = null,
  inlineTaskQueueToggle = false,
  showWorkflowProvenanceInChat = false,
  initialSessionId = null,
  variant = "panel",
}) {
  const {
    panelDividerEdge,
    panelSx,
    panelClassName,
    hidePanelHeader,
    hideSessionHeader,
    conversationClassName,
    messagesClassName,
    splitChatFixedWidth,
  } = PANEL_VARIANTS[variant] || PANEL_VARIANTS.panel;
  const [agentId, setAgentId] = useState(providedAgentId);
  const [loading, setLoading] = useState(!providedAgentId);
  const [error, setError] = useState(null);
  const [newChatNonce, setNewChatNonce] = useState(0);
  const [showSessionsOverlay, setShowSessionsOverlay] = useState(false);
  const [selectSessionRequest, setSelectSessionRequest] = useState(null);
  const [isTaskQueueOpen, setIsTaskQueueOpen] = useState(false);
  const [isTaskQueueToggleVisible, setIsTaskQueueToggleVisible] =
    useState(false);
  const [taskQueueToggleSignal, setTaskQueueToggleSignal] = useState(0);

  const allSessions = useAgentStore((state) => state.sessions);
  const sessionsLoading = useAgentStore((state) => state.sessionsLoading);
  const sessions = useMemo(
    () =>
      allSessions.filter((session) =>
        sessionFilter ? sessionFilter(session) : true
      ),
    [allSessions, sessionFilter]
  );

  useEffect(() => {
    if (providedAgentId) {
      setAgentId(providedAgentId);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const provisionResult = provisionAgent
          ? await provisionAgent()
          : await send_request(provisionUrl, {}, {}, "POST");
        if (cancelled) return;
        if (isRequestError(provisionResult)) {
          setError(
            extractRequestErrorMessage(provisionResult, provisionErrorMessage)
          );
          return;
        }
        const nextAgentId =
          typeof provisionResult === "string"
            ? provisionResult
            : provisionResult?.data?.agentId;
        if (nextAgentId) {
          setAgentId(nextAgentId);
        } else {
          setError(provisionErrorMessage);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || provisionErrorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providedAgentId, provisionAgent, provisionErrorMessage, provisionUrl]);

  useEffect(() => {
    if (!agentId || !initialSessionId) {
      return;
    }
    setSelectSessionRequest({
      sessionId: initialSessionId,
      nonce: Date.now(),
    });
  }, [agentId, initialSessionId]);

  const handleOpenSessions = useCallback(() => {
    if (agentId) {
      useAgentStore.getState().fetchSessions(agentId);
    }
    setShowSessionsOverlay(true);
  }, [agentId]);

  const handleSelectSession = useCallback((chatSessionId) => {
    setShowSessionsOverlay(false);
    setSelectSessionRequest({ sessionId: chatSessionId, nonce: Date.now() });
  }, []);

  const handleTaskQueueToggleStateChange = useCallback((state) => {
    setIsTaskQueueToggleVisible(Boolean(state?.visible));
    setIsTaskQueueOpen(Boolean(state?.open));
  }, []);

  const handleToggleTaskQueue = useCallback(() => {
    setTaskQueueToggleSignal((n) => n + 1);
  }, []);

  const panelStyle = {
    ...embeddedAgentPanelBaseSx,
    ...panelDividerSx[panelDividerEdge],
    ...panelSx,
    ...(isTaskQueueOpen
      ? { width: EMBEDDED_AGENT_PANEL_QUEUE_OPEN_WIDTH }
      : {}),
  };

  if (loading) {
    return (
      <Box
        className={panelClassName || undefined}
        sx={{ ...panelStyle, alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        className={panelClassName || undefined}
        sx={{
          ...panelStyle,
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 2,
        }}
      >
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={panelClassName || undefined} sx={panelStyle}>
      {!hidePanelHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            ...pageHeaderSx,
            boxSizing: "border-box",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {panelTitle || agentName}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {isTaskQueueToggleVisible && (
              <IconButton
                size="small"
                onClick={handleToggleTaskQueue}
                color={isTaskQueueOpen ? "primary" : "default"}
                title={isTaskQueueOpen ? "Hide planning" : "Show planning"}
              >
                <ChecklistIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => setNewChatNonce((n) => n + 1)}
              title="New chat"
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleOpenSessions}
              title="Sessions"
            >
              <ForumIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box
        sx={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}
      >
        <AgentSessionsPanel
          agentId={agentId}
          agentName={agentName}
          agentDescription={agentDescription}
          agentModel={agentModel}
          mode="private"
          showSessionSidebar={false}
          hideHeader={hideSessionHeader}
          conversationClassName={conversationClassName}
          messagesClassName={messagesClassName}
          newChatRequestNonce={newChatNonce}
          selectSessionRequest={selectSessionRequest}
          inlineTaskQueueToggle={inlineTaskQueueToggle}
          composerDisabled={composerDisabled}
          welcomePanelContent={welcomePanelContent}
          examplePrompts={examplePrompts}
          initialComposerDraft={initialComposerDraft}
          autoStartInitialPrompt={autoStartInitialPrompt}
          onTemplateLaunchConsumed={onTemplateLaunchConsumed}
          chatContext={chatContext}
          chatUiPolicy={chatUiPolicy}
          taskQueueToggleSignal={taskQueueToggleSignal}
          onTaskQueueToggleStateChange={handleTaskQueueToggleStateChange}
          splitChatFixedWidth={splitChatFixedWidth}
        />

        {showSessionsOverlay && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "background.paper",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                Sessions
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowSessionsOverlay(false)}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {sessionsLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" height={18} />
                      <Skeleton variant="text" width="50%" height={14} />
                    </Box>
                  ))}
                </Box>
              ) : sessions.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "text.secondary",
                      lineHeight: 1.6,
                    }}
                  >
                    {emptySessionsMessage}
                  </Typography>
                </Box>
              ) : (
                sessions.map((session) => (
                  <SessionRow
                    key={session.chatSessionId}
                    session={session}
                    agentName={agentName}
                    selected={false}
                    onClick={() => handleSelectSession(session.chatSessionId)}
                    showWorkflowProvenanceInChat={showWorkflowProvenanceInChat}
                  />
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
